import {useAuth} from "../../hooks/useAuth";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useQueryClient} from "@tanstack/react-query";
import {Link, useBlocker, useParams, useNavigate} from "react-router-dom";
import Button from "@rescui/button";
import {useTextStyles} from "@rescui/typography";
import {Input} from "@rescui/input";
import "../../layout/Toolbar/index.css";
import {AuthStatus} from "../../layout/Toolbar/AuthStatus";
import {DefaultsMenu} from "../../layout/DefaultScenePanel/Menu";
import {GridModeNavigation} from "../../grid/ui/GridModeNavigation";
import {DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent} from "@dnd-kit/core";
import {SortableContext, rectSortingStrategy, sortableKeyboardCoordinates} from "@dnd-kit/sortable";
import {CHANNELS} from "../../data/products";
import {getVisibleChannels} from "../../utils/roles";
import {BannerInspector} from "../../grid/BannerInspector";
import {GridCard} from "../../grid/ui/GridCard";
import {TemplatePicker} from "../../grid/ui/TemplatePicker";
import {createWorkspace, openGridWorkspace, updateWorkspace, reorderedIds, visibleGridDocument} from "../../grid/workspace";
import {useGridAccess} from "../../grid/access";
import {useGridProject} from "../../grid/useGridProject";
import {loadGridProject, shareGridProject, ProjectRequestError, type SavedProject, type ProjectHead} from "../../grid/projects";
import {GridDialog} from "../../grid/ui/GridDialog";
import {GridAccountRoot} from "../../grid/ui/GridAccountRoot";
import {PreviewBoundary} from "../../grid/ui/PreviewBoundary";
import {loadGridSnapshot, saveGridSnapshot, SnapshotRequestError, type SavedGrid} from "../../grid/snapshots";
import {exportGrid} from "../../grid/export";
import {downloadBlob as saveAs} from "../../libs/serverExport";
import {GRID_MAX_BANNERS, GRID_MAX_BYTES} from "../../grid/limits";
import {type BannerInstance, type GridDocument} from "../../grid/document";
import {createBanner} from "../../grid/defaults";
import {changeBannerFormat} from "../../grid/format";
import {createGridResources, defaultInput} from "../../grid/resources";
import {type BannerSelection} from "../../data/template-availability";
import {type BannerChange} from "../../grid/scope-adapter";
import {type GridCommand} from "../../grid/commands";
import {getTemplateDefinition} from "../../data/template-catalog";
import {getProductBySlug} from "../../data/products/index";
import "../../App.css";
import "../../css/fonts.css";
import "@rescui/colors/lib/index.css";
import "./index.css";
import {ProjectsOverlay} from "../Projects";

function LeaveDialog({onStay, onLeave, reload = false}: {onStay: () => void; onLeave: () => void; reload?: boolean}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {const element = ref.current; element.showModal(); return () => element.close();}, []);
  return <dialog ref={ref} className="grid-leave-dialog" aria-labelledby="leave-grid-title" onCancel={(e) => {e.preventDefault(); onStay();}}>
    <h2 id="leave-grid-title">{reload ? "Open saved version?" : "Leave this grid?"}</h2><p>{reload ? "Opening the saved version will discard the draft in this tab." : "Your unsaved changes are only in this tab. Leaving will discard them."}</p>
    <div><Button mode="outline" autoFocus onClick={onStay}>Keep editing</Button><Button onClick={onLeave}>{reload ? "Open saved version" : "Leave grid"}</Button></div>
  </dialog>;
}

function GridWorkspace({role, initialSaved, initialProject, onProjectCreated, onReload}: {role: string; initialSaved?: SavedGrid; initialProject?: SavedProject; onProjectCreated: (project: ProjectHead) => void; onReload: () => void}) {
  const [defaultsExpanded, setDefaultsExpanded] = useState(false);
  const textCn = useTextStyles();
  const [state, setState] = useState(() => initialProject ? openGridWorkspace(initialProject.document) : initialSaved ? openGridWorkspace(initialSaved.document) : createWorkspace());
  const latestState = useRef(state); latestState.current = state;
  const {featureFlags} = useAuth();
  const readOnly = useGridAccess() === "read-only";
  const editingLocked = useRef(readOnly); editingLocked.current = readOnly;
  const editEpoch = useRef(0);
  const [saved, setSaved] = useState(initialSaved);
  const [sharing, setSharing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false), [shareUncertain, setShareUncertain] = useState(false);
  const sharedDocument = useRef(initialSaved?.document);
  const adoptingPath = useRef<string | null>(null);
  const projectSave = useGridProject(state, setState, initialProject, (project) => {adoptingPath.current = project.editPath; onProjectCreated(project);});
  const saving = projectSave.saving || sharing;
  const [reloadConfirm, setReloadConfirm] = useState(false);
  const [shareError, setShareError] = useState("");
  const [copyNote, setCopyNote] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const exportRequest = useRef<AbortController | null>(null);
  const shareAttempt = useRef<({kind: "snapshot"; document: GridDocument} | {kind: "project"; projectId: string; revision: number; document?: GridDocument}) & {key: string} | null>(null);
  const shareInFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  useEffect(() => () => exportRequest.current?.abort(), []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [inspectorTarget, setInspectorTarget] = useState<HTMLDivElement | null>(null);
  const [pending, setPending] = useState<Record<string, number>>({});
  const [formatChanging, setFormatChanging] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<{instanceId: string; message: string} | null>(null);
  const formatInFlight = useRef(false);
  const [announcement, setAnnouncement] = useState("");
  const workspaceRef = useRef<HTMLDivElement>(null);
  const focusAfterChange = useRef<string | "add" | null>(null);
  const client = useQueryClient();
  const resources = useMemo(() => createGridResources(client), [client]);
  const channels = getVisibleChannels(CHANNELS, role, featureFlags);
  const {selectedId, dirty} = state;
  const document = visibleGridDocument(state);
  const selected = document.banners.find((b) => b.instanceId === selectedId);
  const selectedDefinition = selected ? getTemplateDefinition(selected.templateKey) : null;
  const exportCount = document.banners.filter((b) => b.exportEnabled).length;
  const pendingCount = document.banners.reduce((count, banner) => count + (pending[banner.instanceId] ?? 0), 0);
  const hasPendingEdits = pendingCount > 0 || formatChanging !== null;
  const hasUnsavedWork = dirty || saving || hasPendingEdits || projectSave.retryPending || shareUncertain;
  const blocker = useBlocker(({currentLocation, nextLocation}) => hasUnsavedWork && currentLocation.pathname !== nextLocation.pathname && nextLocation.pathname !== adoptingPath.current);
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const warn = (event: BeforeUnloadEvent) => {event.preventDefault(); event.returnValue = "";};
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedWork]);
  useEffect(() => {
    if (!focusAfterChange.current) return;
    const target = focusAfterChange.current;
    if (target === "add") workspaceRef.current.querySelector<HTMLButtonElement>("[data-grid-add]").focus();
    else workspaceRef.current.querySelector<HTMLElement>(`[data-grid-card="${target}"]`)?.focus();
    focusAfterChange.current = null;
  }, [document, selectedId]);
  const run = useCallback((commands: GridCommand[], selectId?: string) => {if (!editingLocked.current) setState((current) => updateWorkspace(current, {type: "commands", commands, selectId}));}, []);
  const select = useCallback((instanceId: string) => setState((current) => updateWorkspace(current, {type: "select", instanceId})), []);
  const change: BannerChange = useCallback((id, patch) => setState((current) => {
    if (editingLocked.current) return current;
    const banner = current.document.banners.find((b) => b.instanceId === id);
    if (!banner) return current;
    return updateWorkspace(current, {type: "commands", commands: [{type: "updateBanner", instanceId: id, patch: typeof patch === "function" ? patch(banner) : patch}]});
  }), []);
  const pendingChanged = useCallback((id: string, count: number) => setPending((current) => current[id] === count ? current : {...current, [id]: count}), []);
  const changeFormat = async (source: BannerInstance, templateKey: string) => {
    if (editingLocked.current || formatInFlight.current || pending[source.instanceId] || source.templateKey === templateKey) return;
    const epoch = editEpoch.current;
    formatInFlight.current = true; setFormatChanging(source.instanceId); setFormatError(null);
    try {
      const banner = await changeBannerFormat(source, templateKey, resources);
      if (!mounted.current || editingLocked.current || editEpoch.current !== epoch) return;
      if (latestState.current.document.banners.find((entry) => entry.instanceId === source.instanceId) !== source) {
        throw new Error("Banner changed while its format was loading. Try again.");
      }
      setState((current) => current.document.banners.find((entry) => entry.instanceId === source.instanceId) !== source ? current
        : updateWorkspace(current, {type: "commands", commands: [{type: "replaceBanner", banner}]}));
      setAnnouncement("Banner format updated");
    } catch (error) {
      if (mounted.current) setFormatError({instanceId: source.instanceId, message: error instanceof Error ? error.message : "Could not change banner format"});
    } finally {
      formatInFlight.current = false;
      if (mounted.current) setFormatChanging(null);
    }
  };
  const add = async (selections: BannerSelection[]) => {
    if (editingLocked.current) return;
    const epoch = editEpoch.current;
    if (document.banners.length + selections.length > GRID_MAX_BANNERS) throw new Error(`Grid supports at most ${GRID_MAX_BANNERS} banners`);
    const banners = await Promise.all(selections.map((selection) => createBanner(defaultInput(selection), resources)));
    if (!mounted.current || editingLocked.current || editEpoch.current !== epoch) return;
    setState((current) => updateWorkspace(current, {type: "commands", commands: banners.map((banner, index) => ({type: "addBanner", banner, index: current.document.banners.length + index})), selectId: banners[0].instanceId}));
    focusAfterChange.current = banners[0].instanceId;
    setAnnouncement(`${banners.length} banners added`);
  };
  const duplicate = (id: string) => {
    if (document.banners.length >= GRID_MAX_BANNERS) {setShareError(`Grid supports at most ${GRID_MAX_BANNERS} banners`); return;}
    const newId = `banner-${crypto.randomUUID()}`;
    focusAfterChange.current = newId;
    run([{type: "duplicateBanner", instanceId: id, newInstanceId: newId}], newId);
    setAnnouncement("Banner duplicated");
  };
  const remove = (id: string) => {
    const index = document.banners.findIndex((b) => b.instanceId === id);
    const next = document.banners[index + 1] ?? document.banners[index - 1];
    focusAfterChange.current = selectedId === id ? next?.instanceId ?? "add" : selectedId;
    run([{type: "removeBanner", instanceId: id}]);
    setAnnouncement("Banner removed");
  };
  const move = (id: string, overId: string) => {
    run([{type: "reorderBanners", instanceIds: reorderedIds(document, id, overId)}]);
    setAnnouncement("Banner order updated");
  };
  const dragEnd = ({active, over}: DragEndEvent) => {if (over && active.id !== over.id) move(String(active.id), String(over.id));};
  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 8}}), useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}));
  const copyLink = async (snapshot: SavedGrid) => {
    try {await navigator.clipboard.writeText(new URL(snapshot.editPath, window.location.origin).href); if (mounted.current) setCopyNote("Snapshot link copied");} catch {
      if (mounted.current) setCopyNote("Snapshot saved. Copy the link below.");
    }
  };
  const share = async (choice?: "saved" | "save") => {
    if (editingLocked.current || shareInFlight.current || hasPendingEdits || formatInFlight.current) return;
    setShareError(""); setCopyNote("");
    if (!shareAttempt.current && saved && sharedDocument.current === document) {await copyLink(saved); return;}
    if (!shareAttempt.current && projectSave.project && dirty && !choice) {setShareOpen(true); return;}
    if (new TextEncoder().encode(JSON.stringify(document)).byteLength > GRID_MAX_BYTES) {setShareError("Grid document exceeds 4 MiB. Remove some banners or large SVG assets."); return;}
    shareInFlight.current = true; setSharing(true);
    try {
      if (!shareAttempt.current) {
        let head = projectSave.project, source = !dirty ? document : undefined;
        if (choice === "save") {
          const result = await projectSave.save();
          if (!result || !mounted.current) return;
          head = result.head; source = result.document;
        }
        shareAttempt.current = head ? {kind: "project", projectId: head.projectId, revision: head.revision, key: crypto.randomUUID(), document: source}
          : {kind: "snapshot", document, key: crypto.randomUUID()};
      }
      const attempt = shareAttempt.current;
      const result = attempt.kind === "project" ? await shareGridProject(attempt) : await saveGridSnapshot(attempt.document, attempt.key);
      if (!mounted.current) return;
      setSaved(result); sharedDocument.current = attempt.document;
      shareAttempt.current = null; setShareUncertain(false); setShareOpen(false);
      await copyLink(result);
    } catch (reason) {
      if (mounted.current) {
        const definitive = (reason instanceof ProjectRequestError || reason instanceof SnapshotRequestError) && reason.status < 500;
        if (definitive) shareAttempt.current = null;
        setShareUncertain(!definitive);
        setShareError(reason instanceof Error ? reason.message : "Could not share this Grid");
      }
    } finally {shareInFlight.current = false; if (mounted.current) setSharing(false);}
  };
  const download = async () => {
    if (exportRequest.current || hasPendingEdits || formatInFlight.current || !exportCount) return;
    const controller = new AbortController(); exportRequest.current = controller;
    setExporting(true); setExportError("");
    try {
      const result = await exportGrid(document, controller.signal);
      if (mounted.current) saveAs(result.blob, result.filename);
    } catch (error) {if (mounted.current) setExportError(error instanceof Error ? error.message : "Could not export Grid");} finally {
      exportRequest.current = null;
      if (mounted.current) setExporting(false);
    }
  };
  return <div className="App grid-workspace" ref={workspaceRef}><main>
    <aside aria-label="Banner settings" className="grid-sidebar"><div className="toolbar-scroll toolbar-inner">
      <div className="grid-sidebar-header">
      <AuthStatus />
      {readOnly && <p role="status">Grid is temporarily read-only. Saved projects and exports remain available.</p>}
      <div className="toolbar-row grid-workspace-heading"><Link to="/" aria-label="Coverbaker channel workspaces" className={textCn("rs-link")}>Coverbaker</Link> · Custom Grid</div>
      <div className="grid-document-heading">
        <label className="grid-sr-only" htmlFor="grid-name">Grid name</label>
        <Input id="grid-name" aria-label="Grid name" disabled={readOnly} maxLength={120} value={document.title} onChange={(e) => run([{type: "setTitle", title: e.target.value}])} />
        <Button className="btn" size="m" mode="outline" id="save_button" aria-label={projectSave.saving ? "Saving grid…" : projectSave.retryPending ? "Retry save" : "Save grid"}
          disabled={readOnly || saving || hasPendingEdits || projectSave.conflict || Boolean(projectSave.project && !dirty && !projectSave.retryPending)}
          onClick={() => void projectSave.save()}>{projectSave.saving ? "Saving…" : projectSave.retryPending ? "Retry" : "Save grid"}</Button>
      </div>
      {selected && <section className="grid-selected-context" aria-label="Selected banner">
        <strong>{selectedDefinition.label}</strong>
        <span>{getProductBySlug(selected.product).label} · {selectedDefinition.width} × {selectedDefinition.height} · {document.banners.indexOf(selected) + 1} of {document.banners.length}</span>
      </section>}
      </div>
      <div className="grid-sidebar-content">
      <div id="grid-panel-settings" className="grid-sidebar-panel grid-settings-panel">
      <div ref={setInspectorTarget} />
      {formatError?.instanceId === selectedId && <p role="alert">{formatError.message} The banner format has not changed.</p>}
      {!selected && <p className="toolbar-row grid-draft-note">Add a banner to start editing.</p>}
      {document.banners.length > 0 && <div className="toolbar-row grid-export-selection">
        <span className="label">Export selection</span>
        <Button size="s" mode="clear" disabled={readOnly} onClick={() => run(document.banners.map((b) => ({type: "updateBanner", instanceId: b.instanceId, patch: {exportEnabled: true}})))}>Include all</Button>
        <Button size="s" mode="clear" disabled={readOnly} onClick={() => run(document.banners.map((b) => ({type: "updateBanner", instanceId: b.instanceId, patch: {exportEnabled: false}})))}>Clear export selection</Button>
      </div>}
      <p className="grid-draft-note">Save keeps this Grid in My projects. Share creates a snapshot link for signed-in Coverbaker users with access to its resources.</p>
      <DefaultsMenu setLabel={selected?.set || "default"} expanded={defaultsExpanded} onExpandedChange={setDefaultsExpanded} navigation={<GridModeNavigation />} />
      </div>
      </div>
      <div className="grid-sidebar-footer">
      <div className="toolbar-btns">
        <Button className="btn" size="m" mode="outline" id="share_button" aria-label={sharing ? "Sharing snapshot…" : shareUncertain ? "Retry Share" : saved && sharedDocument.current === document ? "Copy snapshot link" : "Share snapshot"}
          disabled={readOnly || saving || hasPendingEdits} onClick={() => void share()}>{sharing ? "Sharing…" : shareUncertain ? "Retry Share" : saved && sharedDocument.current === document ? "Copy link" : "Share"}</Button>
        <Button className="btn" size="m" mode="outline" id="export_button" disabled={exporting || hasPendingEdits || !exportCount} onClick={() => void download()}>{exporting ? "Exporting…" : "Export"}</Button>
      </div>
      {projectSave.error && <p role="alert">{projectSave.error}</p>}
      {projectSave.retryPending && <p className="grid-draft-note">Retry confirms the previous save. Any newer edits stay in this tab and need another Save.</p>}
      {projectSave.conflict && <div className="grid-conflict-actions" aria-label="Save conflict">
        <Button size="s" mode="outline" disabled={readOnly || saving || hasPendingEdits} onClick={() => void projectSave.save(true)}>Save as new project</Button>
        {projectSave.trashed ? <Link to="/projects?trash=true" className={textCn("rs-link")}>Open Trash</Link> : <Button size="s" mode="clear" disabled={saving} onClick={() => setReloadConfirm(true)}>Open saved version</Button>}
      </div>}
      {exportError && <p role="alert">{exportError}</p>}
      {shareError && <p role="alert">{shareError}</p>}
      {shareUncertain && <p className="grid-draft-note">Retry Share confirms the same snapshot request. Newer edits are not included.</p>}
      {copyNote && <p role="status">{copyNote}</p>}
      {saved && <div className="toolbar-row"><a className={textCn("rs-link")} href={saved.editPath} target="_blank" rel="noreferrer">Open saved snapshot</a></div>}
      {!projectSave.project && <p className="grid-draft-note">Not saved to My projects{initialSaved && <> · <button className="grid-save-copy" disabled={readOnly || saving || hasPendingEdits} onClick={() => void projectSave.save()}>Save to My projects</button></>}</p>}
      <div className="grid-draft-note grid-save-status" role="status">{dirty ? "Unsaved draft" : projectSave.project ? "All changes saved" : initialSaved ? "Snapshot opened" : "New grid"} · {document.banners.length} banners · {exportCount} included in export</div>
      </div>
    </div></aside>
    <div className="templates"><div className="templates-inner">
    {!document.banners.length ? <button className="template-wrap grid-add-card" data-grid-add disabled={readOnly} onClick={() => setPickerOpen(true)}>+ Add banners</button>
      : <DndContext sensors={sensors} onDragEnd={dragEnd}><SortableContext items={document.banners.map((b) => b.instanceId)} strategy={rectSortingStrategy}>
        {document.banners.map((banner, index) => <GridCard key={banner.instanceId} banner={banner} readOnly={readOnly || formatChanging === banner.instanceId} index={index} total={document.banners.length} selected={selectedId === banner.instanceId}
          onSelect={() => select(banner.instanceId)} onChange={change} onPendingChange={pendingChanged}
          onDuplicate={() => duplicate(banner.instanceId)} onRemove={() => remove(banner.instanceId)}
          onMove={(offset) => move(banner.instanceId, document.banners[index + offset].instanceId)}
          onNavigate={(offset) => {const next = document.banners[Math.max(0, Math.min(document.banners.length - 1, index + offset))]; workspaceRef.current.querySelector<HTMLElement>(`[data-grid-card="${next.instanceId}"]`).focus();}}
          inspector={selectedId === banner.instanceId && inspectorTarget ? (rootRef) => createPortal(<PreviewBoundary readOnly={readOnly || formatChanging === banner.instanceId}><BannerInspector banner={banner} onChange={change} rootRef={rootRef}
            formatBusy={formatChanging !== null} onFormatChange={(templateKey) => void changeFormat(banner, templateKey)} /></PreviewBoundary>, inspectorTarget) : undefined} />)}
          <button className="template-wrap grid-add-card" data-grid-add disabled={readOnly} onClick={() => setPickerOpen(true)}><span>+</span>Add banners</button>
      </SortableContext></DndContext>}
    {pendingCount > 0 && <p className="grid-upload-status" role="status">Finishing {pendingCount} {pendingCount === 1 ? "change" : "changes"}… You can keep editing.</p>}
    <p className="grid-sr-only" role="status" aria-live="polite">{announcement}</p>
    </div></div></main>
    {pickerOpen && <TemplatePicker channels={channels} role={role} onClose={() => setPickerOpen(false)} onAdd={add} />}
    {shareOpen && <GridDialog title="Share this project" onClose={() => {if (!saving) setShareOpen(false);}}>
      <p>This project has unsaved changes. Choose the saved state to include in the snapshot.</p>
      <p>Recipients must sign in and have access to the Grid's channels and uploaded images.</p>
      {shareError && <p role="alert">{shareError}</p>}{projectSave.error && <p role="alert">{projectSave.error}</p>}
      {projectSave.retryPending && <p>Confirm the pending Save with Retry save in the editor before saving more changes.</p>}
      {shareUncertain && <p>The Share request may have succeeded. Retry confirms the same revision; newer edits are not included.</p>}
      <div className="grid-dialog-actions"><Button mode="outline" disabled={saving} onClick={() => setShareOpen(false)}>Cancel</Button>
        {shareUncertain ? <Button disabled={saving} onClick={() => void share()}>Retry Share</Button> : <>
          <Button mode="outline" disabled={saving || projectSave.retryPending} onClick={() => void share("saved")}>Share last saved</Button>
          <Button disabled={saving || projectSave.retryPending || projectSave.conflict} onClick={() => void share("save")}>Save and share</Button></>}
      </div>
    </GridDialog>}
    {reloadConfirm && <LeaveDialog reload onStay={() => setReloadConfirm(false)} onLeave={onReload} />}
    {blocker.state === "blocked" && <LeaveDialog onStay={() => blocker.reset()} onLeave={() => blocker.proceed()} />}
  </div>;
}
type GridEntry = {requestKey: string; workspaceKey: string; snapshot?: SavedGrid; project?: SavedProject};
function GridRouteWorkspace({role}: {role: string}) {
  const {snapshotId, projectId} = useParams(), navigate = useNavigate();
  const routeKey = projectId ? `project:${projectId}` : snapshotId ? `snapshot:${snapshotId}` : "new";
  const [retry, setRetry] = useState(0);
  const requestKey = `${routeKey}:${retry}`;
  const [entry, setEntry] = useState<GridEntry | null>(() => routeKey === "new" ? {requestKey, workspaceKey: requestKey} : null);
  const [error, setError] = useState<{requestKey: string; message: string} | null>(null);
  useEffect(() => {
    if (entry?.requestKey === requestKey) return;
    const controller = new AbortController();
    const open = async () => {
      try {
        const loaded = projectId ? {project: await loadGridProject(projectId, controller.signal)} : snapshotId ? {snapshot: await loadGridSnapshot(snapshotId, controller.signal)} : {};
        if (!controller.signal.aborted) setEntry({requestKey, workspaceKey: requestKey, ...loaded});
      } catch (reason) {
        if (!controller.signal.aborted) setError({requestKey, message: reason instanceof Error ? reason.message : "Could not open Grid"});
      }
    };
    void open();
    return () => controller.abort();
  }, [requestKey, entry?.requestKey, projectId, snapshotId]);
  if (entry?.requestKey !== requestKey) {
    if (error?.requestKey === requestKey) return <div className="grid-auth-loading"><p role="alert">{error.message}</p><Button onClick={() => setRetry((current) => current + 1)}>Retry</Button> <Link to="/projects">My projects</Link> · <Link to="/grid">New grid</Link></div>;
    return <div className="grid-auth-loading" role="status">Opening saved grid…</div>;
  }
  return <React.Fragment key={entry.workspaceKey}><GridWorkspace role={role} initialSaved={entry.snapshot} initialProject={entry.project}
    onReload={() => setRetry((current) => current + 1)} onProjectCreated={(project) => {
      // Adopt the permanent URL without remounting the draft.
      setEntry((current) => ({...current, requestKey: `project:${project.projectId}:${retry}`}));
      navigate(project.editPath, {replace: true});
    }} /></React.Fragment>;
}
export default function GridRoot() {
  return <GridAccountRoot>{(role) => <><GridRouteWorkspace role={role} /><ProjectsOverlay /></>}</GridAccountRoot>;
}
